import { useState } from 'react'
import {
  Container,
  Typography,
  Button,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
} from '@mui/material'
import { Add, FilterList } from '@mui/icons-material'
import { useTaskStore } from '@/store/useTaskStore'
import { useFamilyStore } from '@/store/useFamilyStore'
import TaskCard from '@/components/tasks/TaskCard'
import TaskForm from '@/components/tasks/TaskForm'
import TaskCompletionDialog from '@/components/tasks/TaskCompletionDialog'
import type { Task } from '@/types'
import { ROOMS } from '@/types'

export default function TasksPage() {
  const [formOpen, setFormOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [completingTask, setCompletingTask] = useState<Task | null>(null)
  const [filterRoom, setFilterRoom] = useState<string>('All')
  const [filterMember, setFilterMember] = useState<string>('All')
  const [searchQuery, setSearchQuery] = useState('')

  const tasks = useTaskStore((state) => state.tasks)
  const members = useFamilyStore((state) => state.members)

  const handleEdit = (task: Task) => {
    setEditingTask(task)
    setFormOpen(true)
  }

  const handleComplete = (task: Task) => {
    setCompletingTask(task)
  }

  const handleCloseForm = () => {
    setFormOpen(false)
    setEditingTask(null)
  }

  // Filter tasks
  const filteredTasks = tasks.filter((task) => {
    if (filterRoom !== 'All' && task.room !== filterRoom) return false
    if (filterMember !== 'All' && task.assigned_to !== filterMember) return false
    if (
      searchQuery &&
      !task.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false
    return true
  })

  // Group tasks by status
  const upcomingTasks = filteredTasks.filter(
    (task) => new Date(task.start_date) >= new Date()
  )
  const overdueTasks = filteredTasks.filter(
    (task) => new Date(task.start_date) < new Date()
  )

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h4">Tasks</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setFormOpen(true)}
          >
            New Task
          </Button>
        </Box>

        {/* Filters */}
        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2 }}
        >
          <FilterList color="action" />

          <TextField
            label="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Room</InputLabel>
            <Select
              value={filterRoom}
              label="Room"
              onChange={(e) => setFilterRoom(e.target.value)}
            >
              <MenuItem value="All">All Rooms</MenuItem>
              {ROOMS.map((room) => (
                <MenuItem key={room} value={room}>
                  {room}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Assigned To</InputLabel>
            <Select
              value={filterMember}
              label="Assigned To"
              onChange={(e) => setFilterMember(e.target.value)}
            >
              <MenuItem value="All">All Members</MenuItem>
              {members.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(filterRoom !== 'All' || filterMember !== 'All' || searchQuery) && (
            <Button
              size="small"
              onClick={() => {
                setFilterRoom('All')
                setFilterMember('All')
                setSearchQuery('')
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Results Count */}
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredTasks.length} of {tasks.length} tasks
          </Typography>
        </Box>
      </Box>

      {/* Overdue Tasks */}
      {overdueTasks.length > 0 && (
        <Box mb={4}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Typography variant="h5">Overdue</Typography>
            <Chip label={overdueTasks.length} color="error" size="small" />
          </Box>
          <Stack spacing={2}>
            {overdueTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                date={new Date(task.start_date)}
                onEdit={handleEdit}
                onComplete={handleComplete}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Upcoming Tasks */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Typography variant="h5">Upcoming</Typography>
          <Chip label={upcomingTasks.length} color="primary" size="small" />
        </Box>
        {upcomingTasks.length === 0 ? (
          <Typography variant="body1" color="text.secondary">
            No upcoming tasks. Create a new task to get started!
          </Typography>
        ) : (
          <Stack spacing={2}>
            {upcomingTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                date={new Date(task.start_date)}
                onEdit={handleEdit}
                onComplete={handleComplete}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Task Form Dialog */}
      <TaskForm open={formOpen} onClose={handleCloseForm} task={editingTask} />

      {/* Task Completion Dialog */}
      <TaskCompletionDialog
        open={Boolean(completingTask)}
        onClose={() => setCompletingTask(null)}
        task={completingTask}
      />
    </Container>
  )
}
